import React from "react";
import  Table from '../base-table';
import type { TableRef } from '../base-table';
import type { ColumnDef, SortingState, ColumnFiltersState, ColumnOrderState, RowSelectionState, PaginationState } from "@tanstack/react-table";
import { getCoreRowModel } from "@tanstack/react-table";
import { Exception } from "../../graphql/graphql";
import { TableMessage } from "../../shared/TableMessage";
import { TableSpinner } from "../../shared/TableSpinner";
import { UISettings } from "../../shared/Types";

interface ExceptionsTableProps {
  tableRef: React.Ref<TableRef>;
  columns?: () => ColumnDef<Exception, any>[];
  data: Exception[];
  tableTitle?: React.ReactNode;
  noDataContent?: React.ReactNode;
  getRowId?: (originalRow: Exception) => string;
  enableRowSelection?: boolean;
  enableColumnFiltersSidePanel?: boolean;
  onPaginationChange?: (pagination: PaginationState) => void;
  pageIndex?: number;
  pageSize?: number;
  totalCount?: number;
  state: {
    sorting: SortingState;
    columnFilters: ColumnFiltersState;
    setSorting: (newSorting: SortingState) => void;
    setColumnFilters: (newFilters: ColumnFiltersState) => void;
    columnOrder: ColumnOrderState;
    setColumnOrder: (newOrder: ColumnOrderState) => void;
    setRowSelection: (newRowSelection: RowSelectionState) => void;
    rowSelection?: RowSelectionState;
    resetRowSelection?: (defaultState?: boolean) => void;
    columnVisibility?: Record<string, boolean>;
    setColumnVisibility?: (newVisibility: Record<string, boolean>) => void;
    pagination?: { totalCount?: number}
    globalSearch?: string;
    columnFiltersSidePanelIsOpen?: boolean;
  };
  isLoading: boolean;
  error: any;
  enableGlobalFilter?: boolean;
  onGlobalFilterChange?: (filter: string) => void;
  meta?: any;
  onColumnFiltersSidePanelOpenChange?: (isOpen: boolean) => void;
  saveTableSettings?: (key: string, settings: UISettings) => Promise<void> | void;
  settingsKey?: string;
  loadTableSettings?: (key: string) => UISettings | null;
}

export const ExceptionsTable = React.memo(
    function ExceptionsTable({ tableRef, 
        columns, 
        data,
        tableTitle,
        noDataContent,
        state, 
        isLoading, 
        error, 
        getRowId , 
        pageIndex = 0, 
        pageSize = 10, 
        totalCount, 
        onPaginationChange, 
        enableGlobalFilter,
        enableColumnFiltersSidePanel,
        meta, 
        onGlobalFilterChange,
        onColumnFiltersSidePanelOpenChange,
        saveTableSettings,
        settingsKey,
        loadTableSettings
    }: ExceptionsTableProps) {

    // if (isLoading) return <Skeleton height={60} width={"100%"} />;
    if (error) return <div style={{ color: "red" }}>Error: {error.message}</div>;

    if (data) {
        return (
            <div style={{ height: "100%" }}>
        <Table
            className="exceptions-table"
            fullWidth
            ref={tableRef}
            columns={columns ? (columns() as any) : []}
            getCoreRowModel={getCoreRowModel()}
            data={data}
            tableTitle={tableTitle}
            noDataContent={isLoading ? <TableSpinner /> : <TableMessage msg="No Exceptions Found" />}
            enableColumnFilters={false}
            enableColumnFiltersSidePanel={enableColumnFiltersSidePanel}
            enableSorting={true}
            disableStickyHeader={false}
            allowMultiSort={true}
            enableRowSelection={true}
            enablePagination={true}
            manualPagination={true}
            manualFiltering={true}
            manualSorting={true}
            enableMultiRowSelection={false}
            enableGlobalFilter={enableGlobalFilter}
            initialGlobalFilter={state.globalSearch}
            onPaginationChange={onPaginationChange}
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalCount={state.pagination?.totalCount}
            state={{
                columnFilters: state.columnFilters,
                sorting: state.sorting,
                columnOrder: state.columnOrder,
                setColumnOrder: state.setColumnOrder,
                rowSelection: state.rowSelection,
                columnVisibility: state.columnVisibility,
                columnFiltersSidePanelIsOpen: state.columnFiltersSidePanelIsOpen,
            }}
            emptyState={{
            title: "No Exceptions Found",
            description: "There are no exceptions to display at this time.",
            }}
            onSortChange={state.setSorting}
            onColumnFiltersChange={state.setColumnFilters}
            onRowSelectionChange={state.setRowSelection}
            onGlobalFilterChange={onGlobalFilterChange}
            onColumnFiltersSidePanelOpenChange={onColumnFiltersSidePanelOpenChange}
            getRowId={getRowId}
            resetRowSelection={state.resetRowSelection}
            meta={meta}
            noDataText={isLoading ? "Loading exceptions..." : "No exceptions found."}
            globalFilterPlaceholder="Search Exceptions..."
            globalFilterLiveSearch={true}
            settingsKey={settingsKey}
            saveTableSettings={saveTableSettings}
            loadTableSettings={loadTableSettings}
         
        />
        </div>
        );
    }
    return <></>;
});

